import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronDown, ChevronUp, HelpCircle, Download, Save, FileText } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import { LOGO_BASE64, LEGAL_PDF_BASE64 } from '@/lib/pdfAssets';
import FeaturesDocument from './FeaturesDocument';

const API_URL = '';

// Tooltip helper component
const InfoTooltip = ({ content, children }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1.5 cursor-help">
          {children}
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#0FFFE2]/20 border border-[#0FFFE2]/50 hover:bg-[#0FFFE2]/40 hover:border-[#0FFFE2] transition-all">
            <HelpCircle className="w-3.5 h-3.5 text-[#0FFFE2]" />
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent 
        side="top" 
        className="max-w-xs bg-[#1a1a1a] border border-[#0FFFE2]/30 text-white p-3 rounded-lg shadow-lg shadow-[#0FFFE2]/10"
      >
        {content}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Tooltip content definitions
const tooltipContent = {
  purchaseRate: (
    <div className="space-y-2">
      <p className="font-semibold text-[#0FFFE2]">How to Calculate Purchase Rate:</p>
      <p className="text-sm">(Total Purchases ÷ Total Website Visitors) × 100</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#D0FF0B]">Example:</span> If 50 people bought out of 2,000 visitors = 2.5%</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#FF7901]">Where to find:</span> Google Analytics → Conversions, Shopify Analytics, or your payment processor</p>
    </div>
  ),
  costPerClick: (
    <div className="space-y-2">
      <p className="font-semibold text-[#0FFFE2]">How to Calculate Cost Per Click:</p>
      <p className="text-sm">Total Ad Spend ÷ Total Clicks</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#D0FF0B]">Example:</span> $500 spent ÷ 250 clicks = $2 CPC</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#FF7901]">Where to find:</span> Facebook Ads Manager, Google Ads, TikTok Ads dashboard</p>
    </div>
  ),
  optinRate: (
    <div className="space-y-2">
      <p className="font-semibold text-[#0FFFE2]">How to Calculate Opt-in Rate:</p>
      <p className="text-sm">(Total Opt-ins ÷ Total Page Visitors) × 100</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#D0FF0B]">Example:</span> 150 opt-ins from 3,000 visitors = 5%</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#FF7901]">Where to find:</span> Your landing page builder (ClickFunnels, Leadpages) or email platform</p>
    </div>
  ),
  leadToSale: (
    <div className="space-y-2">
      <p className="font-semibold text-[#0FFFE2]">How to Calculate Lead → Sale Rate:</p>
      <p className="text-sm">(Total Sales ÷ Total Leads/Opt-ins) × 100</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#D0FF0B]">Example:</span> 10 sales from 200 leads = 5%</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#FF7901]">Where to find:</span> Compare your email list size to your payment processor sales count</p>
    </div>
  ),
  costPerLead: (
    <div className="space-y-2">
      <p className="font-semibold text-[#0FFFE2]">How to Calculate Cost Per Lead:</p>
      <p className="text-sm">Total Ad Spend ÷ Total Leads Generated</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#D0FF0B]">Example:</span> $1,000 ad spend ÷ 40 leads = $25 CPL</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#FF7901]">Where to find:</span> Your ads dashboard compared to new email subscribers</p>
    </div>
  ),
  revenueGoal: (
    <div className="space-y-2">
      <p className="font-semibold text-[#0FFFE2]">Revenue Goal</p>
      <p className="text-sm">The total amount of money you want to make from sales.</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#D0FF0B]">Tip:</span> Be specific! Instead of "more money," say "$50,000 this quarter"</p>
    </div>
  ),
  pricePerUnit: (
    <div className="space-y-2">
      <p className="font-semibold text-[#0FFFE2]">Price Per Sale</p>
      <p className="text-sm">How much does a customer pay for one purchase?</p>
      <p className="text-xs text-white/70 mt-2"><span className="text-[#D0FF0B]">Tip:</span> Use your average order value if you have multiple products</p>
    </div>
  ),
};

const OfferCalculator = ({ loadedSession, onClearLoadedSession }) => {
  // Form state
  const [revenueGoal, setRevenueGoal] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [timeframe, setTimeframe] = useState('months');
  const [timeframeValue, setTimeframeValue] = useState('');
  
  // Calculation method choice
  const [calculationMethod, setCalculationMethod] = useState('conversion'); // 'conversion' or 'organic'
  
  // Sales funnel type: 'direct' (traffic → buy) or 'optin' (traffic → opt-in → sale)
  const [funnelType, setFunnelType] = useState('optin');
  
  // Save/PDF state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showFeaturesDoc, setShowFeaturesDoc] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const resultsRef = useRef(null);

  // Load session when loadedSession changes
  useEffect(() => {
    if (loadedSession && loadedSession.inputs) {
      const inputs = loadedSession.inputs;
      setRevenueGoal(inputs.revenueGoal?.toString() || '');
      setPricePerUnit(inputs.pricePerSale?.toString() || '');
      setTimeframe(inputs.timeframe || 'months');
      setTimeframeValue(inputs.timeframeValue?.toString() || '');
      setCalculationMethod(inputs.calculationMethod || 'conversion');
      setFunnelType(inputs.funnelType || 'optin');
      setWebsiteConversion(inputs.websiteConversion?.toString() || '');
      setLeadConversion(inputs.leadConversion?.toString() || '');
      setCostPerLead(inputs.costPerLead?.toString() || '');
      setSelectedIndustry(inputs.selectedIndustry || 'coaching');
      setUseIndustryStandards(inputs.useIndustryStandards || false);
      setImprovedWebsiteConversion(inputs.improvedWebsiteConversion || 5);
      setImprovedLeadConversion(inputs.improvedLeadConversion || 10);
      // Trigger calculation
      setTimeout(() => setCalculated(true), 100);
      if (onClearLoadedSession) onClearLoadedSession();
    }
  }, [loadedSession, onClearLoadedSession]);
  
  // Conversion metrics
  const [websiteConversion, setWebsiteConversion] = useState('');
  const [leadConversion, setLeadConversion] = useState('');
  const [costPerLead, setCostPerLead] = useState('');
  const [useIndustryStandards, setUseIndustryStandards] = useState(false);
  
  // Organic reach inputs
  const [socialFollowing, setSocialFollowing] = useState('');
  const [emailListSize, setEmailListSize] = useState('');
  const [socialEngagementRate, setSocialEngagementRate] = useState('');
  const [emailOpenRate, setEmailOpenRate] = useState('');
  const [emailClickRate, setEmailClickRate] = useState('');
  
  // What-if analysis
  const [improvedWebsiteConversion, setImprovedWebsiteConversion] = useState(5);
  const [improvedLeadConversion, setImprovedLeadConversion] = useState(10);
  
  // UI state
  const [showDetails, setShowDetails] = useState(false);
  const [calculated, setCalculated] = useState(false);

  // Industry baseline values - Research-backed benchmarks (2024-2025 data)
  // Sources: FirstPageSage, Ruler Analytics, HubSpot, WordStream, Unbounce
  // directConversion = visitor directly to sale (e-com style)
  // websiteConversion = visitor to lead/opt-in
  // leadConversion = lead/opt-in to sale
  const industryBaselines = {
    automotive: { 
      name: 'Automotive', 
      directConversion: '2.5',
      websiteConversion: '3.2', 
      leadConversion: '5', 
      costPerLead: '295' 
    },
    b2bservices: { 
      name: 'B2B Professional Services', 
      directConversion: '1.5',
      websiteConversion: '2.5', 
      leadConversion: '5', 
      costPerLead: '350' 
    },
    beauty: { 
      name: 'Beauty / Skincare', 
      directConversion: '3.2',
      websiteConversion: '3.2', 
      leadConversion: '5', 
      costPerLead: '85' 
    },
    coaching: { 
      name: 'Coaching / Consulting', 
      directConversion: '2',
      websiteConversion: '5', 
      leadConversion: '7', 
      costPerLead: '150' 
    },
    ecommerce: { 
      name: 'E-Commerce / Retail', 
      directConversion: '2.5',
      websiteConversion: '2.5', 
      leadConversion: '4', 
      costPerLead: '91' 
    },
    education: { 
      name: 'Education / Online Courses', 
      directConversion: '2',
      websiteConversion: '4.5', 
      leadConversion: '5', 
      costPerLead: '982' 
    },
    finance: { 
      name: 'Finance / Insurance', 
      directConversion: '2.6',
      websiteConversion: '5.2', 
      leadConversion: '2.6', 
      costPerLead: '761' 
    },
    fitness: { 
      name: 'Fitness / Wellness', 
      directConversion: '2.5',
      websiteConversion: '4', 
      leadConversion: '6', 
      costPerLead: '120' 
    },
    foodbev: { 
      name: 'Food & Beverage', 
      directConversion: '3.1',
      websiteConversion: '3.1', 
      leadConversion: '4', 
      costPerLead: '75' 
    },
    healthcare: { 
      name: 'Healthcare / Medical', 
      directConversion: '2.5',
      websiteConversion: '3.1', 
      leadConversion: '5', 
      costPerLead: '361' 
    },
    homeservices: { 
      name: 'Home Services / HVAC', 
      directConversion: '3',
      websiteConversion: '3.8', 
      leadConversion: '6', 
      costPerLead: '95' 
    },
    legal: { 
      name: 'Legal Services', 
      directConversion: '3',
      websiteConversion: '4', 
      leadConversion: '7.4', 
      costPerLead: '649' 
    },
    agency: { 
      name: 'Marketing / Creative Agency', 
      directConversion: '2.5',
      websiteConversion: '3.5', 
      leadConversion: '8', 
      costPerLead: '185' 
    },
    nonprofit: { 
      name: 'Nonprofit / Charity', 
      directConversion: '3',
      websiteConversion: '4', 
      leadConversion: '8', 
      costPerLead: '50' 
    },
    realestate: { 
      name: 'Real Estate', 
      directConversion: '1.5',
      websiteConversion: '2.4', 
      leadConversion: '2', 
      costPerLead: '448' 
    },
    saas: { 
      name: 'SaaS / Software', 
      directConversion: '2',
      websiteConversion: '3', 
      leadConversion: '1.2', 
      costPerLead: '237' 
    },
    travel: { 
      name: 'Travel / Hospitality', 
      directConversion: '3.6',
      websiteConversion: '3.6', 
      leadConversion: '4', 
      costPerLead: '155' 
    },
  };

  // Selected industry - default to coaching
  const [selectedIndustry, setSelectedIndustry] = useState('coaching');

  // Handle industry standards toggle
  const handleIndustryStandardsToggle = (enabled) => {
    setUseIndustryStandards(enabled);
    if (enabled) {
      const industry = industryBaselines[selectedIndustry];
      if (funnelType === 'direct') {
        setWebsiteConversion(industry.directConversion);
        setLeadConversion('');
      } else {
        setWebsiteConversion(industry.websiteConversion);
        setLeadConversion(industry.leadConversion);
      }
      setCostPerLead(industry.costPerLead);
    } else {
      setWebsiteConversion('');
      setLeadConversion('');
      setCostPerLead('');
    }
    setCalculated(false);
  };

  // Handle industry change
  const handleIndustryChange = (industryKey) => {
    setSelectedIndustry(industryKey);
    if (useIndustryStandards) {
      const industry = industryBaselines[industryKey];
      if (funnelType === 'direct') {
        setWebsiteConversion(industry.directConversion);
        setLeadConversion('');
      } else {
        setWebsiteConversion(industry.websiteConversion);
        setLeadConversion(industry.leadConversion);
      }
      setCostPerLead(industry.costPerLead);
      setCalculated(false);
    }
  };

  // Handle funnel type change
  const handleFunnelTypeChange = (type) => {
    setFunnelType(type);
    if (useIndustryStandards) {
      const industry = industryBaselines[selectedIndustry];
      if (type === 'direct') {
        setWebsiteConversion(industry.directConversion);
        setLeadConversion('');
      } else {
        setWebsiteConversion(industry.websiteConversion);
        setLeadConversion(industry.leadConversion);
      }
    } else if (type === 'direct') {
      setLeadConversion('');
    }
    setCalculated(false);
  };

  // Format input value with commas
  const formatInputValue = (value) => {
    if (!value) return '';
    const numericValue = value.replace(/[^0-9.]/g, '');
    if (!numericValue) return '';
    const parts = numericValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const parseInputValue = (formattedValue) => {
    return formattedValue.replace(/,/g, '');
  };

  const handleNumberInput = (value, setter) => {
    const rawValue = parseInputValue(value);
    setter(rawValue);
    setCalculated(false);
  };

  // Calculate results
  const results = useMemo(() => {
    const revenue = parseFloat(revenueGoal) || 0;
    const price = parseFloat(pricePerUnit) || 0;
    const tf = parseFloat(timeframeValue) || 1;

    if (!revenue || !price || !calculated) {
      return null;
    }

    // Total sales needed
    const totalSales = Math.ceil(revenue / price);

    // Convert timeframe to days for consistent calculations
    let totalDays;
    switch (timeframe) {
      case 'days':
        totalDays = tf;
        break;
      case 'weeks':
        totalDays = tf * 7;
        break;
      case 'months':
      default:
        totalDays = tf * 30;
        break;
    }

    // Sales breakdown - what you need per time period within your timeframe
    const dailySales = totalSales / totalDays;
    const weeklySales = dailySales * 7;
    const monthlySales = dailySales * 30;
    const quarterlySales = dailySales * 90;
    const annualSales = dailySales * 365;

    // Get conversion rates based on method
    let webConv = 0;
    let leadConv = 0;
    let cpl = 0;

    if (calculationMethod === 'conversion') {
      webConv = parseFloat(websiteConversion) / 100 || 0;
      // Only use lead conversion for opt-in funnels
      leadConv = funnelType === 'optin' ? (parseFloat(leadConversion) / 100 || 0) : 0;
      cpl = parseFloat(costPerLead) || 0;
    } else {
      // Organic method - use fixed assumptions
      webConv = 0.02; // 2% website conversion
      leadConv = funnelType === 'optin' ? 0.02 : 0; // 2% lead conversion only for opt-in
      cpl = 0; // No ad spend
    }

    // Traffic & Leads calculations
    let totalLeads = 0;
    let totalVisitors = 0;
    
    if (funnelType === 'direct') {
      // Direct purchase: Traffic → Website → Buy
      // webConv is the direct purchase rate
      if (webConv > 0) {
        totalVisitors = Math.ceil(totalSales / webConv);
      }
      totalLeads = 0; // No leads in direct model
    } else {
      // Opt-in funnel: Traffic → Opt-in → Sale
      if (leadConv > 0) {
        totalLeads = Math.ceil(totalSales / leadConv);
      }
      if (webConv > 0 && totalLeads > 0) {
        totalVisitors = Math.ceil(totalLeads / webConv);
      } else if (webConv > 0) {
        totalVisitors = Math.ceil(totalSales / webConv);
      }
    }

    // Visitors/Leads per time period
    const dailyVisitors = totalVisitors / totalDays;
    const weeklyVisitors = dailyVisitors * 7;
    const monthlyVisitors = dailyVisitors * 30;
    const annualVisitors = dailyVisitors * 365;

    const dailyLeads = totalLeads / totalDays;
    const weeklyLeads = dailyLeads * 7;
    const monthlyLeads = dailyLeads * 30;
    const annualLeads = dailyLeads * 365;

    // Financial calculations
    // For direct purchase: cost is per visitor/click
    // For opt-in: cost is per lead
    const totalMarketingCost = cpl > 0 ? (funnelType === 'direct' ? totalVisitors * cpl : totalLeads * cpl) : 0;
    const customerAcquisitionCost = totalSales > 0 && totalMarketingCost > 0 ? totalMarketingCost / totalSales : 0;
    const roas = totalMarketingCost > 0 ? revenue / totalMarketingCost : 0;
    const netProfit = revenue - totalMarketingCost;

    // What-if analysis
    const improvedWebConv = improvedWebsiteConversion / 100;
    const improvedLeadConv = improvedLeadConversion / 100;
    
    let improvedLeads = totalLeads;
    let improvedVisitors = totalVisitors;
    
    if (funnelType === 'direct') {
      // Direct: just improved website/purchase conversion
      if (improvedWebConv > 0) {
        improvedVisitors = Math.ceil(totalSales / improvedWebConv);
      }
      improvedLeads = 0;
    } else {
      // Opt-in: both conversions matter
      if (improvedLeadConv > 0) {
        improvedLeads = Math.ceil(totalSales / improvedLeadConv);
      }
      if (improvedWebConv > 0 && improvedLeads > 0) {
        improvedVisitors = Math.ceil(improvedLeads / improvedWebConv);
      }
    }
    
    const improvedMarketingCost = cpl > 0 ? (funnelType === 'direct' ? improvedVisitors * cpl : improvedLeads * cpl) : 0;

    // Organic reach calculations - using user-provided rates
    const socialFollowers = parseFloat(socialFollowing) || 0;
    const emailSubscribers = parseFloat(emailListSize) || 0;
    const totalAudience = socialFollowers + emailSubscribers;
    
    // Get user-provided engagement rates (convert from percentage)
    const socialEngRate = parseFloat(socialEngagementRate) / 100 || 0;
    const emailOpen = parseFloat(emailOpenRate) / 100 || 0;
    const emailClick = parseFloat(emailClickRate) / 100 || 0;
    
    // Calculate potential visitors based on user's actual rates
    const potentialSocialVisitors = Math.floor(socialFollowers * socialEngRate);
    const potentialEmailVisitors = Math.floor(emailSubscribers * emailOpen * emailClick);
    const totalOrganicVisitors = potentialSocialVisitors + potentialEmailVisitors;
    
    const organicCoverage = totalVisitors > 0 ? (totalOrganicVisitors / totalVisitors) * 100 : 0;
    const trafficGap = Math.max(0, totalVisitors - totalOrganicVisitors);
    
    // Calculate audience needed based on user's engagement rate
    const avgEngagementRate = totalAudience > 0 
      ? (totalOrganicVisitors / totalAudience) 
      : 0.04; // fallback
    const audienceNeeded = totalVisitors > 0 && avgEngagementRate > 0 
      ? Math.ceil(totalVisitors / avgEngagementRate) 
      : 0;

    return {
      totalSales,
      totalDays,
      dailySales,
      weeklySales,
      monthlySales,
      quarterlySales,
      annualSales,
      totalVisitors,
      totalLeads,
      dailyVisitors,
      weeklyVisitors,
      monthlyVisitors,
      annualVisitors,
      dailyLeads,
      weeklyLeads,
      monthlyLeads,
      annualLeads,
      totalMarketingCost,
      customerAcquisitionCost,
      roas,
      netProfit,
      improvedVisitors,
      improvedMarketingCost,
      websiteConversionRate: webConv * 100,
      leadConversionRate: leadConv * 100,
      revenue,
      pricePerUnit: price,
      timeframeDisplay: `${tf} ${timeframe}`,
      hasCpl: cpl > 0,
      totalAudience,
      potentialSocialVisitors,
      potentialEmailVisitors,
      totalOrganicVisitors,
      organicCoverage,
      trafficGap,
      audienceNeeded,
      hasOrganic: totalAudience > 0
    };
  }, [revenueGoal, pricePerUnit, timeframe, timeframeValue, websiteConversion, leadConversion, costPerLead, calculated, improvedWebsiteConversion, improvedLeadConversion, socialFollowing, emailListSize, calculationMethod, socialEngagementRate, emailOpenRate, emailClickRate, funnelType]);

  const handleCalculate = useCallback(() => {
    setCalculated(true);
  }, []);

  // Save calculation to database
  const handleSaveCalculation = async () => {
    if (!saveName.trim()) {
      setSaveMessage({ type: 'error', text: 'Please enter a name for this calculation' });
      return;
    }

    setSaving(true);
    setSaveMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/api/auth/calculations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: saveName,
          inputs: {
            revenueGoal: parseFloat(revenueGoal) || 0,
            pricePerSale: parseFloat(pricePerUnit) || 0,
            timeframe,
            timeframeValue: parseFloat(timeframeValue) || 0,
            calculationMethod,
            funnelType,
            websiteConversion: parseFloat(websiteConversion) || 0,
            leadConversion: parseFloat(leadConversion) || 0,
            costPerLead: parseFloat(costPerLead) || 0,
            selectedIndustry,
            useIndustryStandards,
            improvedWebsiteConversion,
            improvedLeadConversion
          },
          results: results ? {
            totalSalesNeeded: results.totalSales,
            totalVisitors: results.totalVisitors,
            totalLeads: results.totalLeads,
            totalMarketingCost: results.totalMarketingCost,
            roas: results.roas,
            netProfit: results.netProfit
          } : {}
        })
      });

      if (!response.ok) throw new Error('Failed to save');

      setSaveMessage({ type: 'success', text: 'Calculation saved!' });
      setTimeout(() => {
        setShowSaveModal(false);
        setSaveName('');
        setSaveMessage({ type: '', text: '' });
      }, 1500);
    } catch (err) {
      setSaveMessage({ type: 'error', text: 'Failed to save calculation' });
    } finally {
      setSaving(false);
    }
  };

  // Generate PDF report
  const handleDownloadPDF = async () => {
    if (!results) return;

    try {
      console.log('Starting PDF generation with embedded assets...');
      
      // Use embedded Base64 assets (no network fetch needed)
      const logoDataUrl = LOGO_BASE64;
      
      // Convert Base64 legal PDF to ArrayBuffer
      const base64ToArrayBuffer = (base64) => {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
      };
      
      const legalPdfBytes = base64ToArrayBuffer(LEGAL_PDF_BASE64);
      
      console.log('Assets loaded from embedded Base64, generating PDF...');

      // Step 2: Create the report PDF with jsPDF (US Letter to match legal PDF)
      const pdf = new jsPDF('p', 'mm', 'letter');
      const pageWidth = pdf.internal.pageSize.getWidth(); // 215.9mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 279.4mm
      const margin = 15;
      const footerHeight = 15;
      const contentBottom = pageHeight - footerHeight - 10; // Leave space for footer
      let yPos = 15;
      let currentPage = 1;

      // Helper function to check if we need a new page
      const checkPageBreak = (neededSpace = 20) => {
        if (yPos + neededSpace > contentBottom) {
          // Add footer to current page
          pdf.setFillColor(0, 0, 0);
          pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text(`Generated by UnCloned® Offer Calculator | ${new Date().toLocaleDateString()} | Page ${currentPage}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
          
          // Add new page
          pdf.addPage('letter');
          currentPage++;
          yPos = 20;
          return true;
        }
        return false;
      };

      // Helper functions
      const setColor = (hex) => {
        const cleanHex = hex.replace('#', '');
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return [r, g, b];
      };

      const addText = (text, x, y, options = {}) => {
        pdf.setFontSize(options.size || 10);
        if (options.color) {
          const [r, g, b] = setColor(options.color);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(0, 0, 0);
        }
        if (options.bold) pdf.setFont('helvetica', 'bold');
        else pdf.setFont('helvetica', 'normal');
        pdf.text(String(text), x, y, options.align ? { align: options.align } : undefined);
      };

      const addLine = (y, color = '#DF1283') => {
        const [r, g, b] = setColor(color);
        pdf.setDrawColor(r, g, b);
        pdf.setLineWidth(0.5);
        pdf.line(margin, y, pageWidth - margin, y);
      };

      const formatCurr = (num) => '$' + Number(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      const formatNum = (num) => Number(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
      const formatPct = (num) => Number(num).toFixed(1) + '%';

      // ============ PAGE 1: RESULTS ============
      // Black header background
      pdf.setFillColor(0, 0, 0);
      pdf.rect(0, 0, pageWidth, 45, 'F');
      
      // Add logo with correct aspect ratio (original: 1291 x 282, ratio: 4.58)
      if (logoDataUrl) {
        try {
          const logoWidth = 80;
          const logoHeight = logoWidth / 4.58; // Maintain aspect ratio (~17.5mm)
          const logoX = (pageWidth - logoWidth) / 2;
          const logoY = (45 - logoHeight) / 2; // Center vertically in header
          pdf.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
          console.log('Logo added to PDF');
        } catch (logoErr) {
          console.error('Failed to add logo:', logoErr);
        }
      }
      
      // Subtitle
      pdf.setFontSize(10);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Revenue Planning Report', pageWidth / 2, 40, { align: 'center' });

      yPos = 55;

      // ============ SUMMARY BOX ============
      pdf.setFillColor(245, 245, 245);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 28, 2, 2, 'F');
      
      const colWidth = (pageWidth - margin * 2) / 3;
      
      addText('TOTAL SALES NEEDED', margin + 5, yPos + 8, { size: 8, color: '#666666' });
      addText(formatNum(results.totalSales), margin + 5, yPos + 18, { size: 18, bold: true, color: '#DF1283' });
      
      addText('REVENUE GOAL', margin + colWidth + 5, yPos + 8, { size: 8, color: '#666666' });
      addText(formatCurr(results.revenue), margin + colWidth + 5, yPos + 18, { size: 14, bold: true, color: '#4A0A66' });
      
      addText('TIMEFRAME', margin + colWidth * 2 + 5, yPos + 8, { size: 8, color: '#666666' });
      addText(results.timeframeDisplay, margin + colWidth * 2 + 5, yPos + 18, { size: 12, bold: true, color: '#FF7901' });

      yPos += 38;

      // ============ YOUR INPUTS ============
      checkPageBreak(50);
      addText('YOUR INPUTS', margin, yPos, { size: 12, bold: true, color: '#4A0A66' });
      addLine(yPos + 3);
      yPos += 12;

      const inputsList = [
        ['Revenue Goal', formatCurr(results.revenue)],
        ['Price Per Sale', formatCurr(results.pricePerUnit)],
        ['Timeframe', results.timeframeDisplay],
        ['Funnel Type', funnelType === 'direct' ? 'Direct Purchase' : 'Opt-in First'],
        ['Industry', industryBaselines[selectedIndustry]?.name || selectedIndustry],
        [funnelType === 'direct' ? 'Purchase Rate' : 'Opt-in Rate', formatPct(results.websiteConversionRate)],
      ];
      
      if (funnelType === 'optin') {
        inputsList.push(['Lead to Sale Rate', formatPct(results.leadConversionRate)]);
      }
      
      if (results.hasCpl) {
        inputsList.push([funnelType === 'direct' ? 'Cost Per Click' : 'Cost Per Lead', formatCurr(parseFloat(costPerLead))]);
      }

      inputsList.forEach(([label, value]) => {
        checkPageBreak(8);
        addText(label + ':', margin, yPos, { size: 9, color: '#666666' });
        addText(value, margin + 55, yPos, { size: 9, bold: true, color: '#000000' });
        yPos += 6;
      });

      yPos += 8;

      // ============ SALES BREAKDOWN ============
      checkPageBreak(40);
      addText('SALES BREAKDOWN', margin, yPos, { size: 12, bold: true, color: '#4A0A66' });
      addLine(yPos + 3);
      yPos += 12;

      pdf.setFillColor(250, 250, 250);
      pdf.roundedRect(margin, yPos - 3, pageWidth - (margin * 2), 22, 2, 2, 'F');

      const salesData = [
        ['Daily', Math.ceil(results.dailySales)],
        ['Weekly', Math.ceil(results.weeklySales)],
        ['Monthly', Math.ceil(results.monthlySales)],
        ['Quarterly', Math.ceil(results.quarterlySales)]
      ];

      const saleColWidth = (pageWidth - margin * 2) / 4;
      salesData.forEach(([label, value], i) => {
        const x = margin + (saleColWidth * i) + 5;
        addText(label, x, yPos + 5, { size: 8, color: '#666666' });
        addText(formatNum(value) + ' sales', x, yPos + 13, { size: 10, bold: true, color: '#DF1283' });
      });

      yPos += 30;

      // ============ TRAFFIC & LEADS ============
      checkPageBreak(50);
      addText('TRAFFIC & LEADS REQUIRED', margin, yPos, { size: 12, bold: true, color: '#4A0A66' });
      addLine(yPos + 3);
      yPos += 12;

      const trafficData = [
        ['Total Visitors Needed', formatNum(results.totalVisitors)],
        ['Monthly Visitors', formatNum(Math.ceil(results.monthlyVisitors))],
        ['Weekly Visitors', formatNum(Math.ceil(results.weeklyVisitors))],
        ['Daily Visitors', formatNum(Math.ceil(results.dailyVisitors))]
      ];

      if (funnelType === 'optin' && results.totalLeads > 0) {
        trafficData.push(['Total Leads to Convert', formatNum(results.totalLeads)]);
        trafficData.push(['Monthly Leads', formatNum(Math.ceil(results.monthlyLeads))]);
      }

      trafficData.forEach(([label, value]) => {
        checkPageBreak(8);
        addText(label + ':', margin, yPos, { size: 9, color: '#666666' });
        addText(value, margin + 55, yPos, { size: 9, bold: true, color: '#4A0A66' });
        yPos += 6;
      });

      yPos += 8;

      // ============ FINANCIAL IMPACT ============
      if (results.hasCpl) {
        checkPageBreak(60);
        addText('FINANCIAL IMPACT', margin, yPos, { size: 12, bold: true, color: '#4A0A66' });
        addLine(yPos + 3);
        yPos += 12;

        const financialData = [
          ['Estimated Ad Spend', formatCurr(results.totalMarketingCost)],
          ['Customer Acquisition Cost', formatCurr(results.customerAcquisitionCost)],
          ['Return on Ad Spend (ROAS)', results.roas.toFixed(2) + 'x'],
          ['Break-Even ROAS', '1.00x'],
          ['Projected Revenue', formatCurr(results.revenue)],
          ['Projected Net Profit', formatCurr(results.netProfit)]
        ];

        financialData.forEach(([label, value]) => {
          checkPageBreak(8);
          addText(label + ':', margin, yPos, { size: 9, color: '#666666' });
          let color = '#000000';
          if (label.includes('Profit')) color = results.netProfit > 0 ? '#4A0A66' : '#DF1283';
          if (label.includes('ROAS') && !label.includes('Break')) color = results.roas >= 1 ? '#4A0A66' : '#DF1283';
          addText(value, margin + 60, yPos, { size: 9, bold: true, color });
          yPos += 6;
        });

        yPos += 8;
      }

      // ============ WHAT-IF ANALYSIS ============
      checkPageBreak(40);
      addText('WHAT-IF ANALYSIS', margin, yPos, { size: 12, bold: true, color: '#4A0A66' });
      addLine(yPos + 3);
      yPos += 12;

      const whatIfData = [
        [`If ${funnelType === 'direct' ? 'Purchase' : 'Opt-in'} Rate improved to ${improvedWebsiteConversion}%`, 
         `${formatNum(results.improvedVisitors || 0)} visitors needed (${formatNum((results.totalVisitors - (results.improvedVisitors || results.totalVisitors)))} fewer)`],
        [funnelType === 'optin' ? `If Lead Conversion improved to ${improvedLeadConversion}%` : null,
         funnelType === 'optin' ? `${formatNum(results.improvedLeads || 0)} leads needed` : null]
      ].filter(([label]) => label);

      whatIfData.forEach(([label, value]) => {
        checkPageBreak(15);
        addText(label, margin, yPos, { size: 8, color: '#666666' });
        yPos += 5;
        addText(value, margin + 5, yPos, { size: 9, bold: true, color: '#4A0A66' });
        yPos += 8;
      });

      // Final page footer
      pdf.setFillColor(0, 0, 0);
      pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Generated by UnCloned® Offer Calculator | ${new Date().toLocaleDateString()} | Page ${currentPage}`, pageWidth / 2, pageHeight - 6, { align: 'center' });

      console.log('Report page created, merging with legal PDF...');

      // Step 3: Convert jsPDF to bytes and merge with legal PDF using pdf-lib
      const reportPdfBytes = pdf.output('arraybuffer');
      
      // Load both PDFs into pdf-lib
      const reportPdf = await PDFDocument.load(reportPdfBytes);
      const legalPdf = await PDFDocument.load(legalPdfBytes);
      
      // Create a new PDF and copy pages
      const mergedPdf = await PDFDocument.create();
      
      // Copy report pages
      const reportPages = await mergedPdf.copyPages(reportPdf, reportPdf.getPageIndices());
      reportPages.forEach(page => mergedPdf.addPage(page));
      
      // Copy ALL legal pages
      const legalPageCount = legalPdf.getPageCount();
      console.log(`Legal PDF has ${legalPageCount} pages`);
      
      const legalPages = await mergedPdf.copyPages(legalPdf, legalPdf.getPageIndices());
      legalPages.forEach(page => mergedPdf.addPage(page));
      
      console.log(`Merged PDF has ${mergedPdf.getPageCount()} total pages`);
      
      // Save and download
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `UnCloned_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('PDF downloaded successfully!');
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF: ' + error.message);
    }
  };

  const formatNumber = (num, decimals = 0) => {
    if (num === undefined || num === null || isNaN(num)) return '-';
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  const formatCurrency = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '-';
    return num.toLocaleString('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const TimeframeButton = ({ value, label }) => (
    <button
      type="button"
      onClick={() => { setTimeframe(value); setCalculated(false); }}
      data-testid={`toggle-${value}`}
      className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 ${
        timeframe === value 
          ? 'bg-[#DF1283] text-white' 
          : 'bg-white/10 text-white/70 hover:bg-white/20'
      } ${value === 'months' ? 'rounded-l-xl' : value === 'days' ? 'rounded-r-xl' : ''}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-black">
      <div className="px-4 py-12 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Header */}
        <header className="text-center mb-12">
          <img 
            src="/calculator-logo.png" 
            alt="UnCloned® Offer Calculator"
            data-testid="calculator-logo"
            className="max-w-sm sm:max-w-md mx-auto mb-6"
          />
          <p className="text-lg text-white max-w-2xl mx-auto leading-relaxed">
            Ready to hit your revenue goals like a pro? Enter your sales goals and see exactly how much traffic you need to make it happen.
          </p>
        </header>

        {/* SECTION 1: Enter Your Goals */}
        <div className="bg-black rounded-3xl p-6 sm:p-8 border border-white/10 shadow-[0_0_40px_rgba(223,18,131,0.15)] mb-6" data-testid="goals-section">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <span className="w-10 h-10 min-w-[2.5rem] rounded-full bg-[#DF1283] flex items-center justify-center text-lg font-black shrink-0">1</span>
            <span>Enter Your Goals</span>
          </h2>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-[#D0FF0B] uppercase tracking-wider">
                  <InfoTooltip content={tooltipContent.revenueGoal}>
                    Revenue Goal ($)
                  </InfoTooltip>
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg font-semibold">$</span>
                  <Input
                    data-testid="input-revenue-goal"
                    type="text"
                    inputMode="numeric"
                    placeholder="100,000"
                    value={formatInputValue(revenueGoal)}
                    onChange={(e) => handleNumberInput(e.target.value, setRevenueGoal)}
                    className="h-14 pl-8 bg-white/5 border-2 border-[#4A0A66] focus:border-[#D0FF0B] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl"
                  />
                </div>
                <p className="text-xs text-white">How much revenue do you want to generate?</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-[#D0FF0B] uppercase tracking-wider">
                  <InfoTooltip content={tooltipContent.pricePerUnit}>
                    Price Per Sale ($)
                  </InfoTooltip>
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg font-semibold">$</span>
                  <Input
                    data-testid="input-price-per-unit"
                    type="text"
                    inputMode="numeric"
                    placeholder="997"
                    value={formatInputValue(pricePerUnit)}
                    onChange={(e) => handleNumberInput(e.target.value, setPricePerUnit)}
                    className="h-14 pl-8 bg-white/5 border-2 border-[#4A0A66] focus:border-[#D0FF0B] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl"
                  />
                </div>
                <p className="text-xs text-white">How much does your product/service cost?</p>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-semibold text-[#D0FF0B] uppercase tracking-wider">
                Timeframe
              </Label>
              <p className="text-xs text-white">How long do you want to reach this goal?</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Input
                  data-testid="input-timeframe-value"
                  type="text"
                  inputMode="numeric"
                  placeholder="3"
                  value={timeframeValue}
                  onChange={(e) => { setTimeframeValue(e.target.value.replace(/[^0-9]/g, '')); setCalculated(false); }}
                  className="w-full sm:w-24 h-14 bg-white/5 border-2 border-[#4A0A66] focus:border-[#D0FF0B] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl text-center"
                />
                <div className="flex flex-1 border-2 border-[#4A0A66] rounded-xl overflow-hidden" data-testid="timeframe-toggle">
                  <TimeframeButton value="months" label="Months" />
                  <TimeframeButton value="weeks" label="Weeks" />
                  <TimeframeButton value="days" label="Days" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: Choose Calculation Method */}
        <div className="bg-black rounded-3xl p-6 sm:p-8 border border-white/10 shadow-[0_0_40px_rgba(255,121,1,0.15)] mb-6" data-testid="method-section">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <span className="w-10 h-10 min-w-[2.5rem] rounded-full bg-[#FF7901] flex items-center justify-center text-lg font-black shrink-0">2</span>
            <span>How Do You Want to Calculate Traffic?</span>
          </h2>
          
          {/* Method Toggle - Stack on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => { setCalculationMethod('conversion'); setCalculated(false); }}
              data-testid="method-conversion"
              className={`p-6 rounded-2xl border-2 transition-all duration-300 text-left ${
                calculationMethod === 'conversion' 
                  ? 'border-[#0FFFE2] bg-[#0FFFE2]/10' 
                  : 'border-white/10 hover:border-white/30'
              }`}
            >
              <p className="text-lg font-bold text-white mb-2">Use Conversion Rates</p>
              <p className="text-sm text-white/70">I know my website conversion rate, lead conversion, or I want to use industry baselines</p>
            </button>
            <button
              onClick={() => { setCalculationMethod('organic'); setCalculated(false); }}
              data-testid="method-organic"
              className={`p-6 rounded-2xl border-2 transition-all duration-300 text-left ${
                calculationMethod === 'organic' 
                  ? 'border-[#D0FF0B] bg-[#D0FF0B]/10' 
                  : 'border-white/10 hover:border-white/30'
              }`}
            >
              <p className="text-lg font-bold text-white mb-2">Use My Audience Size</p>
              <p className="text-sm text-white/70">I want to see if my social following and email list can hit my goals without ads</p>
            </button>
          </div>

          {/* Conversion Method Inputs */}
          {calculationMethod === 'conversion' && (
            <div className="space-y-6">
              {/* Funnel Type Selection */}
              <div className="p-5 rounded-xl bg-white/5 border border-[#0FFFE2]/30">
                <p className="text-base font-semibold text-white mb-4">How do customers buy from you?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => handleFunnelTypeChange('direct')}
                    data-testid="funnel-direct"
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      funnelType === 'direct' 
                        ? 'border-[#0FFFE2] bg-[#0FFFE2]/10 shadow-[0_0_20px_rgba(15,255,226,0.2)]' 
                        : 'border-white/20 bg-white/5 hover:border-white/40'
                    }`}
                  >
                    <p className="text-base font-bold text-white mb-1">Direct Purchase</p>
                    <p className="text-sm text-white/70">Traffic goes straight to my website and buys (e-commerce, product pages)</p>
                  </button>
                  <button
                    onClick={() => handleFunnelTypeChange('optin')}
                    data-testid="funnel-optin"
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      funnelType === 'optin' 
                        ? 'border-[#0FFFE2] bg-[#0FFFE2]/10 shadow-[0_0_20px_rgba(15,255,226,0.2)]' 
                        : 'border-white/20 bg-white/5 hover:border-white/40'
                    }`}
                  >
                    <p className="text-base font-bold text-white mb-1">Opt-in First</p>
                    <p className="text-sm text-white/70">People opt-in, then I nurture them to a sale (webinars, courses, coaching)</p>
                  </button>
                </div>
              </div>

              {/* Industry Standards Toggle */}
              <div className="p-5 rounded-xl bg-white/5 border border-[#FF7901]/30">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-base font-semibold text-white">Don't know your numbers yet?</span>
                  <button
                    onClick={() => handleIndustryStandardsToggle(!useIndustryStandards)}
                    data-testid="toggle-industry-standards"
                    className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                      useIndustryStandards ? 'bg-[#D0FF0B]' : 'bg-white/20'
                    }`}
                  >
                    <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${
                      useIndustryStandards ? 'left-8' : 'left-1'
                    }`} />
                  </button>
                </div>
                
                {/* Industry Dropdown - Shows when toggle is ON */}
                {useIndustryStandards && (
                  <div className="mt-4 space-y-3">
                    <label className="text-sm font-semibold text-[#FF7901] uppercase tracking-wider">
                      Select Your Industry
                    </label>
                    <select
                      value={selectedIndustry}
                      onChange={(e) => handleIndustryChange(e.target.value)}
                      data-testid="industry-dropdown"
                      className="w-full h-12 px-4 bg-black border-2 border-[#FF7901] focus:border-[#D0FF0B] text-white text-base font-medium rounded-xl appearance-none cursor-pointer outline-none"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23FF7901'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                    >
                      {Object.entries(industryBaselines).map(([key, industry]) => (
                        <option key={key} value={key} className="bg-black text-white">
                          {industry.name}
                        </option>
                      ))}
                    </select>
                    
                    {/* Show selected industry rates based on funnel type */}
                    <div className="mt-3 p-3 rounded-lg bg-[#FF7901]/10 border border-[#FF7901]/30">
                      <p className="text-sm text-[#FF7901] font-medium mb-2">
                        {industryBaselines[selectedIndustry].name} Benchmarks:
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        {funnelType === 'direct' ? (
                          <span className="text-white/80">
                            <span className="text-[#0FFFE2] font-bold">{industryBaselines[selectedIndustry].directConversion}%</span> purchase rate
                          </span>
                        ) : (
                          <>
                            <span className="text-white/80">
                              <span className="text-[#0FFFE2] font-bold">{industryBaselines[selectedIndustry].websiteConversion}%</span> opt-in rate
                            </span>
                            <span className="text-white/80">
                              <span className="text-[#0FFFE2] font-bold">{industryBaselines[selectedIndustry].leadConversion}%</span> lead → sale
                            </span>
                          </>
                        )}
                        <span className="text-white/80">
                          <span className="text-[#0FFFE2] font-bold">${industryBaselines[selectedIndustry].costPerLead}</span> CPL
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {!useIndustryStandards && (
                  <p className="text-sm text-white/70">
                    Toggle on to use industry-specific conversion rates
                  </p>
                )}
              </div>
              
              <div className={`grid grid-cols-1 ${funnelType === 'optin' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-[#0FFFE2] uppercase tracking-wider">
                    <InfoTooltip content={funnelType === 'direct' ? tooltipContent.purchaseRate : tooltipContent.optinRate}>
                      {funnelType === 'direct' ? 'Purchase Rate (%)' : 'Opt-in Rate (%)'}
                    </InfoTooltip>
                  </Label>
                  <Input
                    data-testid="input-website-conversion"
                    type="text"
                    inputMode="decimal"
                    placeholder={funnelType === 'direct' ? '2.5' : '5'}
                    value={websiteConversion}
                    onChange={(e) => { setWebsiteConversion(e.target.value.replace(/[^0-9.]/g, '')); setCalculated(false); }}
                    className={`h-12 bg-white/5 border-2 ${useIndustryStandards ? 'border-[#FF7901]' : 'border-[#4A0A66]'} focus:border-[#0FFFE2] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl`}
                  />
                  <p className="text-xs text-white">{funnelType === 'direct' ? 'Visitors → Purchase' : 'Visitors → Opt-in'}</p>
                </div>

                {funnelType === 'optin' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-[#0FFFE2] uppercase tracking-wider">
                      <InfoTooltip content={tooltipContent.leadToSale}>
                        Lead → Sale (%)
                      </InfoTooltip>
                    </Label>
                    <Input
                      data-testid="input-lead-conversion"
                      type="text"
                      inputMode="decimal"
                      placeholder="7"
                      value={leadConversion}
                      onChange={(e) => { setLeadConversion(e.target.value.replace(/[^0-9.]/g, '')); setCalculated(false); }}
                      className={`h-12 bg-white/5 border-2 ${useIndustryStandards ? 'border-[#FF7901]' : 'border-[#4A0A66]'} focus:border-[#0FFFE2] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl`}
                    />
                    <p className="text-xs text-white">Opt-ins → Sales</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-[#0FFFE2] uppercase tracking-wider">
                    <InfoTooltip content={funnelType === 'direct' ? tooltipContent.costPerClick : tooltipContent.costPerLead}>
                      {funnelType === 'direct' ? 'Cost Per Click ($)' : 'Cost Per Lead ($)'}
                    </InfoTooltip>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm">$</span>
                    <Input
                      data-testid="input-cost-per-lead"
                      type="text"
                      inputMode="numeric"
                      placeholder="25"
                      value={formatInputValue(costPerLead)}
                      onChange={(e) => handleNumberInput(e.target.value, setCostPerLead)}
                      className={`h-12 pl-7 bg-white/5 border-2 ${useIndustryStandards ? 'border-[#FF7901]' : 'border-[#4A0A66]'} focus:border-[#0FFFE2] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl`}
                    />
                  </div>
                  <p className="text-xs text-white">Optional - for ROI calculations</p>
                </div>
              </div>
            </div>
          )}

          {/* Organic Method Inputs */}
          {calculationMethod === 'organic' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-[#D0FF0B]/10 border border-[#D0FF0B]/30">
                <p className="text-sm text-white">
                  Enter your audience size AND your engagement rates below. We'll calculate exactly how much traffic you can generate organically.
                </p>
              </div>
              
              {/* Audience Size */}
              <div>
                <p className="text-sm font-semibold text-[#D0FF0B] mb-4">Your Audience</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-white uppercase tracking-wider">
                      Social Media Following
                    </Label>
                    <Input
                      data-testid="input-social-following"
                      type="text"
                      inputMode="numeric"
                      placeholder="10,000"
                      value={formatInputValue(socialFollowing)}
                      onChange={(e) => handleNumberInput(e.target.value, setSocialFollowing)}
                      className="h-12 bg-white/5 border-2 border-[#4A0A66] focus:border-[#D0FF0B] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl"
                    />
                    <p className="text-xs text-white/70">Combined across all platforms</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-white uppercase tracking-wider">
                      Email List Size
                    </Label>
                    <Input
                      data-testid="input-email-list"
                      type="text"
                      inputMode="numeric"
                      placeholder="5,000"
                      value={formatInputValue(emailListSize)}
                      onChange={(e) => handleNumberInput(e.target.value, setEmailListSize)}
                      className="h-12 bg-white/5 border-2 border-[#4A0A66] focus:border-[#D0FF0B] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl"
                    />
                    <p className="text-xs text-white/70">Total email subscribers</p>
                  </div>
                </div>
              </div>

              {/* Engagement Rates */}
              <div>
                <p className="text-sm font-semibold text-[#0FFFE2] mb-4">Your Engagement Rates</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-white uppercase tracking-wider">
                      Social Engagement (%)
                    </Label>
                    <Input
                      data-testid="input-social-engagement"
                      type="text"
                      inputMode="decimal"
                      placeholder="3"
                      value={socialEngagementRate}
                      onChange={(e) => { setSocialEngagementRate(e.target.value.replace(/[^0-9.]/g, '')); setCalculated(false); }}
                      className="h-12 bg-white/5 border-2 border-[#4A0A66] focus:border-[#0FFFE2] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl"
                    />
                    <p className="text-xs text-white/70">% of followers who click through</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-white uppercase tracking-wider">
                      Email Open Rate (%)
                    </Label>
                    <Input
                      data-testid="input-email-open"
                      type="text"
                      inputMode="decimal"
                      placeholder="20"
                      value={emailOpenRate}
                      onChange={(e) => { setEmailOpenRate(e.target.value.replace(/[^0-9.]/g, '')); setCalculated(false); }}
                      className="h-12 bg-white/5 border-2 border-[#4A0A66] focus:border-[#0FFFE2] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl"
                    />
                    <p className="text-xs text-white/70">% of emails that get opened</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-white uppercase tracking-wider">
                      Email Click Rate (%)
                    </Label>
                    <Input
                      data-testid="input-email-click"
                      type="text"
                      inputMode="decimal"
                      placeholder="2.5"
                      value={emailClickRate}
                      onChange={(e) => { setEmailClickRate(e.target.value.replace(/[^0-9.]/g, '')); setCalculated(false); }}
                      className="h-12 bg-white/5 border-2 border-[#4A0A66] focus:border-[#0FFFE2] text-white text-lg font-semibold placeholder:text-white/30 rounded-xl"
                    />
                    <p className="text-xs text-white/70">% of openers who click</p>
                  </div>
                </div>
                <p className="text-xs text-[#FF7901] mt-3">Don't know your rates? Check your email platform analytics or social media insights.</p>
              </div>
            </div>
          )}
        </div>

        {/* Calculate Button */}
        <Button 
          onClick={handleCalculate}
          data-testid="btn-calculate"
          className="w-full h-16 bg-[#D0FF0B] hover:bg-[#e5ff4d] text-black font-black text-xl uppercase tracking-wider rounded-2xl shadow-[0_0_40px_rgba(208,255,11,0.4)] hover:shadow-[0_0_60px_rgba(208,255,11,0.6)] transition-all duration-300 mb-4"
        >
          Calculate Results
        </Button>

        {/* Save & Download Buttons - Only show after calculation */}
        {results && (
          <div className="flex gap-4 mb-8">
            <Button 
              onClick={() => setShowSaveModal(true)}
              data-testid="btn-save"
              className="flex-1 h-12 bg-[#4A0A66] hover:bg-[#6B0F94] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all"
            >
              <Save className="w-5 h-5" />
              Save Session
            </Button>
            <Button 
              onClick={handleDownloadPDF}
              data-testid="btn-download-pdf"
              className="flex-1 h-12 bg-gradient-to-r from-[#DF1283] to-[#FF7901] hover:opacity-90 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </Button>
          </div>
        )}

        {/* Features Document Button */}
        <div className="flex justify-center mb-8">
          <Button
            onClick={() => setShowFeaturesDoc(true)}
            data-testid="btn-features-doc"
            className="h-10 px-6 bg-transparent border border-[#4A0A66] hover:bg-[#4A0A66]/20 text-gray-300 hover:text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-all"
          >
            <FileText className="w-4 h-4" />
            Download Features PDF
          </Button>
        </div>

        {/* Features Document Modal */}
        {showFeaturesDoc && (
          <FeaturesDocument onClose={() => setShowFeaturesDoc(false)} />
        )}

        {/* Save Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-md">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-[#4A0A66] to-[#DF1283] rounded-2xl opacity-50 blur-sm" />
              <div className="relative bg-black/95 rounded-2xl p-6 border border-[#4A0A66]/30">
                <h3 className="text-xl font-bold text-white mb-4">Save Calculation</h3>
                
                {saveMessage.text && (
                  <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
                    saveMessage.type === 'error'
                      ? 'bg-[#DF1283]/10 border border-[#DF1283]/50 text-[#DF1283]'
                      : 'bg-[#0FFFE2]/10 border border-[#0FFFE2]/50 text-[#0FFFE2]'
                  }`}>
                    {saveMessage.text}
                  </div>
                )}

                <input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g., Q1 Revenue Plan"
                  className="w-full px-4 py-3 bg-[#0a0014] border border-[#4A0A66]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0FFFE2] mb-4"
                />

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSaveModal(false);
                      setSaveName('');
                      setSaveMessage({ type: '', text: '' });
                    }}
                    className="flex-1 py-3 text-gray-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveCalculation}
                    disabled={saving}
                    className="flex-1 py-3 bg-gradient-to-r from-[#0FFFE2] to-[#D0FF0B] text-black font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* RESULTS SECTION - Only shows after calculation */}
        {results && (
          <div className="space-y-6">
            {/* Total Sales Needed - Hero */}
            <div className="bg-black rounded-3xl p-12 border border-[#DF1283]/30 shadow-[0_0_60px_rgba(223,18,131,0.25)] relative overflow-hidden" data-testid="results-section">
              <div className="absolute inset-0 bg-gradient-to-br from-[#DF1283]/10 via-transparent to-[#9333EA]/10 pointer-events-none" />
              
              <div className="relative text-center">
                <p className="text-sm font-bold text-white uppercase tracking-widest mb-8">Total Sales Needed</p>
                <div 
                  className="text-9xl sm:text-[10rem] lg:text-[12rem] font-black mb-8 bg-gradient-to-r from-[#FF1493] via-[#DF1283] to-[#9333EA] bg-clip-text text-transparent leading-none" 
                  data-testid="result-total-sales"
                  style={{
                    filter: 'drop-shadow(0 0 40px rgba(255, 20, 147, 0.7)) drop-shadow(0 0 80px rgba(147, 51, 234, 0.5))',
                    animation: 'glow-text 2.5s ease-in-out infinite'
                  }}
                >
                  {formatNumber(results.totalSales)}
                </div>
                <div className="flex flex-wrap justify-center gap-8 text-lg mb-6">
                  <div>
                    <span className="text-white/70">Revenue Goal</span>
                    <span className="ml-2 text-white font-bold text-xl">{formatCurrency(results.revenue)}</span>
                  </div>
                  <div>
                    <span className="text-white/70">Timeframe</span>
                    <span className="ml-2 text-white font-bold text-xl">{results.timeframeDisplay}</span>
                  </div>
                </div>
                {useIndustryStandards && calculationMethod === 'conversion' && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF7901]/20 border border-[#FF7901]/30">
                    <span className="text-xs text-[#FF7901] font-medium">
                      Using industry baseline estimates (worst-case scenario)
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Sales Breakdown - MASSIVE Hero Style */}
            <div className="bg-black rounded-3xl p-10 border border-white/10 shadow-[0_0_60px_rgba(223,18,131,0.15)]" data-testid="sales-breakdown-section">
              <h3 className="text-2xl font-bold text-white mb-4">Sales Breakdown</h3>
              <p className="text-base text-white/70 mb-8">To hit your goal of {formatNumber(results.totalSales)} sales in {results.timeframeDisplay}, here's your pace:</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {/* Daily - always show */}
                <div className="bg-white/5 rounded-3xl p-8 text-center border border-[#DF1283]/40 shadow-[0_0_30px_rgba(223,18,131,0.2)]">
                  <p className="text-lg text-white mb-4 uppercase tracking-widest font-medium">Daily</p>
                  <p className="text-7xl lg:text-8xl font-black text-[#DF1283] leading-none">{formatNumber(results.dailySales, 1)}</p>
                  <p className="text-lg text-white/60 mt-4">sales/day</p>
                </div>

                {/* Weekly - show if timeframe >= 7 days */}
                {results.totalDays >= 7 && (
                  <div className="bg-white/5 rounded-3xl p-8 text-center border border-[#FF7901]/40 shadow-[0_0_30px_rgba(255,121,1,0.2)]">
                    <p className="text-lg text-white mb-4 uppercase tracking-widest font-medium">Weekly</p>
                    <p className="text-7xl lg:text-8xl font-black text-[#FF7901] leading-none">{formatNumber(results.weeklySales, 1)}</p>
                    <p className="text-lg text-white/60 mt-4">sales/week</p>
                  </div>
                )}

                {/* Monthly - show if timeframe >= 30 days */}
                {results.totalDays >= 30 && (
                  <div className="bg-white/5 rounded-3xl p-8 text-center border border-[#0FFFE2]/40 shadow-[0_0_30px_rgba(15,255,226,0.2)]">
                    <p className="text-lg text-white mb-4 uppercase tracking-widest font-medium">Monthly</p>
                    <p className="text-7xl lg:text-8xl font-black text-[#0FFFE2] leading-none">{formatNumber(results.monthlySales, 1)}</p>
                    <p className="text-lg text-white/60 mt-4">sales/month</p>
                  </div>
                )}

                {/* Quarterly - show if timeframe >= 90 days */}
                {results.totalDays >= 90 && (
                  <div className="bg-white/5 rounded-3xl p-8 text-center border border-[#D0FF0B]/40 shadow-[0_0_30px_rgba(208,255,11,0.2)]">
                    <p className="text-lg text-white mb-4 uppercase tracking-widest font-medium">Quarterly</p>
                    <p className="text-7xl lg:text-8xl font-black text-[#D0FF0B] leading-none">{formatNumber(results.quarterlySales, 1)}</p>
                    <p className="text-lg text-white/60 mt-4">sales/quarter</p>
                  </div>
                )}

                {/* Annual - show if timeframe >= 365 days */}
                {results.totalDays >= 365 && (
                  <div className="bg-white/5 rounded-3xl p-8 text-center border border-white/40 shadow-[0_0_30px_rgba(255,255,255,0.1)]">
                    <p className="text-lg text-white mb-4 uppercase tracking-widest font-medium">Annual</p>
                    <p className="text-7xl lg:text-8xl font-black text-white leading-none">{formatNumber(results.annualSales, 1)}</p>
                    <p className="text-lg text-white/60 mt-4">sales/year</p>
                  </div>
                )}
              </div>
            </div>

            {/* Traffic & Leads Needed */}
            <div className="bg-black rounded-3xl p-6 sm:p-8 border border-white/10" data-testid="traffic-section">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-10 h-10 min-w-[2.5rem] rounded-full bg-[#FF7901] flex items-center justify-center text-lg font-black shrink-0">3</span>
                <span>Traffic & Leads</span>
              </h3>
              
              {/* Top 3 Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#DF1283]">
                  <p className="text-sm text-[#DF1283] font-semibold mb-2">Total Website Visitors</p>
                  <p className="text-4xl font-black text-white mb-2" data-testid="result-total-visitors">
                    {formatNumber(results.totalVisitors)}
                  </p>
                  <p className="text-sm text-white/60">Required to reach your goal</p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#FF7901]">
                  <p className="text-sm text-[#FF7901] font-semibold mb-2">Total Leads Needed</p>
                  <p className="text-4xl font-black text-white mb-2" data-testid="result-total-leads">
                    {formatNumber(results.totalLeads)}
                  </p>
                  <p className="text-sm text-white/60">Required to reach your goal</p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#DF1283]">
                  <p className="text-sm text-[#DF1283] font-semibold mb-2">Leads to Convert</p>
                  <p className="text-4xl font-black text-white mb-2" data-testid="result-conversions">
                    {formatNumber(results.totalSales)}
                  </p>
                  <p className="text-sm text-white/60">To hit your sales target</p>
                </div>
              </div>

              {/* Conversion Rate Progress Bars */}
              <div className="space-y-4 mb-8">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-base text-white font-medium flex items-center gap-2">
                      Visitor to Lead Conversion
                      <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/60">?</span>
                    </span>
                    <span className="text-base text-white font-bold">{websiteConversion || '0'}%</span>
                  </div>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#DF1283] to-[#FF7901] rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(parseFloat(websiteConversion) || 0, 100)}%` }}
                    />
                  </div>
                </div>
                
                {funnelType === 'optin' && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-base text-white font-medium flex items-center gap-2">
                        Lead to Customer Conversion
                        <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/60">?</span>
                      </span>
                      <span className="text-base text-white font-bold">{leadConversion || '0'}%</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#DF1283] to-[#4A0A66] rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(parseFloat(leadConversion) || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Daily/Weekly/Monthly Cards Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Daily */}
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#0FFFE2]">
                  <p className="text-sm text-[#0FFFE2] font-semibold mb-2">Daily Visitors</p>
                  <p className="text-3xl font-black text-white mb-3">{formatNumber(results.dailyVisitors, 0)}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0FFFE2] rounded-full" style={{ width: '60%' }} />
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#0FFFE2]">
                  <p className="text-sm text-[#0FFFE2] font-semibold mb-2">Daily Leads</p>
                  <p className="text-3xl font-black text-white mb-3">{formatNumber(results.dailyLeads, 1)}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0FFFE2] rounded-full" style={{ width: '60%' }} />
                  </div>
                </div>

                {/* Weekly */}
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#D0FF0B]">
                  <p className="text-sm text-[#D0FF0B] font-semibold mb-2">Weekly Visitors</p>
                  <p className="text-3xl font-black text-white mb-3">{formatNumber(results.weeklyVisitors, 0)}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D0FF0B] rounded-full" style={{ width: '75%' }} />
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#D0FF0B]">
                  <p className="text-sm text-[#D0FF0B] font-semibold mb-2">Weekly Leads</p>
                  <p className="text-3xl font-black text-white mb-3">{formatNumber(results.weeklyLeads, 1)}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D0FF0B] rounded-full" style={{ width: '75%' }} />
                  </div>
                </div>

                {/* Monthly */}
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#A855F7]">
                  <p className="text-sm text-[#A855F7] font-semibold mb-2">Monthly Visitors</p>
                  <p className="text-3xl font-black text-white mb-3">{formatNumber(results.monthlyVisitors, 0)}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#A855F7] rounded-full" style={{ width: '90%' }} />
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#A855F7]">
                  <p className="text-sm text-[#A855F7] font-semibold mb-2">Monthly Leads</p>
                  <p className="text-3xl font-black text-white mb-3">{formatNumber(results.monthlyLeads, 1)}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#A855F7] rounded-full" style={{ width: '90%' }} />
                  </div>
                </div>

                {/* Total (like 5-Day in original) */}
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#FF7901]">
                  <p className="text-sm text-[#FF7901] font-semibold mb-2">Total Visitors</p>
                  <p className="text-3xl font-black text-white mb-3">{formatNumber(results.totalVisitors, 0)}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF7901] rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border-l-4 border-[#FF7901]">
                  <p className="text-sm text-[#FF7901] font-semibold mb-2">Total Leads</p>
                  <p className="text-3xl font-black text-white mb-3">{formatNumber(results.totalLeads, 0)}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF7901] rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Organic Reach Analysis - Only for organic method */}
            {calculationMethod === 'organic' && results.hasOrganic && (
              <div className="bg-black rounded-3xl p-8 border border-[#D0FF0B]/30 shadow-[0_0_40px_rgba(208,255,11,0.15)]" data-testid="organic-results">
                <h3 className="text-xl font-bold text-white mb-6">Your Organic Reach Analysis</h3>
                
                {/* Coverage meter */}
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-white font-medium">Your Organic Coverage</span>
                    <span className={`text-3xl font-black ${results.organicCoverage >= 100 ? 'text-[#D0FF0B]' : results.organicCoverage >= 50 ? 'text-[#FF7901]' : 'text-[#DF1283]'}`}>
                      {Math.min(100, results.organicCoverage).toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        results.organicCoverage >= 100 ? 'bg-[#D0FF0B]' : results.organicCoverage >= 50 ? 'bg-[#FF7901]' : 'bg-[#DF1283]'
                      }`}
                      style={{ width: `${Math.min(100, results.organicCoverage)}%` }}
                    />
                  </div>
                  <p className="text-sm text-white mt-3">
                    {results.organicCoverage >= 100 
                      ? "Your audience can potentially generate enough traffic to hit your goals without ads!"
                      : results.organicCoverage >= 50
                      ? "You're halfway there! Your organic reach covers about half of what you need."
                      : "You'll need to grow your audience or supplement with paid traffic."
                    }
                  </p>
                </div>

                {/* How We Calculated This */}
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-6">
                  <p className="text-sm font-semibold text-white mb-3">How We Calculated Your Traffic:</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white/70">Social: {formatNumber(parseFloat(socialFollowing) || 0)} followers × {socialEngagementRate || 0}% engagement</span>
                      <span className="text-[#0FFFE2] font-bold">= {formatNumber(results.potentialSocialVisitors)} visitors</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white/70">Email: {formatNumber(parseFloat(emailListSize) || 0)} × {emailOpenRate || 0}% open × {emailClickRate || 0}% click</span>
                      <span className="text-[#0FFFE2] font-bold">= {formatNumber(results.potentialEmailVisitors)} visitors</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-white font-medium">Total Organic Traffic</span>
                      <span className="text-[#D0FF0B] font-black text-lg">= {formatNumber(results.totalOrganicVisitors)} visitors</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <p className="text-xs text-white mb-1">Traffic Needed</p>
                    <p className="text-xl font-black text-white">{formatNumber(results.totalVisitors)}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <p className="text-xs text-white mb-1">Your Organic Traffic</p>
                    <p className="text-xl font-black text-[#D0FF0B]">{formatNumber(results.totalOrganicVisitors)}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <p className="text-xs text-white mb-1">Traffic Gap</p>
                    <p className={`text-xl font-black ${results.trafficGap > 0 ? 'text-[#DF1283]' : 'text-[#D0FF0B]'}`}>
                      {results.trafficGap > 0 ? formatNumber(results.trafficGap) : 'None!'}
                    </p>
                  </div>
                </div>

                {results.organicCoverage < 100 && results.audienceNeeded > 0 && (
                  <div className="mt-6 p-4 rounded-xl bg-[#DF1283]/10 border border-[#DF1283]/30">
                    <p className="text-sm text-white font-medium mb-2">To hit your goals organically with your current engagement rates:</p>
                    <p className="text-2xl font-black text-[#DF1283]">{formatNumber(results.audienceNeeded)} total audience needed</p>
                    <p className="text-xs text-white/70 mt-1">That's {formatNumber(results.audienceNeeded - results.totalAudience)} more than your current {formatNumber(results.totalAudience)}</p>
                  </div>
                )}
              </div>
            )}

            {/* Financial Impact - Only for conversion method with CPL */}
            {calculationMethod === 'conversion' && results.hasCpl && (
              <div className="bg-black rounded-3xl p-8 border border-[#D0FF0B]/40 shadow-[0_0_60px_rgba(208,255,11,0.15)]" data-testid="financial-section">
                <h3 className="text-2xl font-bold text-white mb-8">Financial Impact</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Total Marketing Cost */}
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                    <p className="text-base text-white/70 mb-2">Total Marketing Cost</p>
                    <p className="text-3xl sm:text-4xl font-black text-[#D0FF0B] mb-3">{formatCurrency(results.totalMarketingCost)}</p>
                    <p className="text-sm text-white/60 mb-2">Based on your cost per lead</p>
                    <p className="text-sm text-white/50 italic">
                      {funnelType === 'direct' 
                        ? `Total Visitors (${formatNumber(results.totalVisitors)}) × Cost Per Click ($${costPerLead})`
                        : `Total Leads (${formatNumber(results.totalLeads)}) × Cost Per Lead ($${costPerLead})`
                      }
                    </p>
                  </div>

                  {/* Customer Acquisition Cost */}
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                    <p className="text-base text-white/70 mb-2">Customer Acquisition Cost</p>
                    <p className="text-3xl sm:text-4xl font-black text-[#FF7901] mb-3">{formatCurrency(results.customerAcquisitionCost)}</p>
                    <p className="text-sm text-white/60 mb-2">Cost to acquire one customer</p>
                    <p className="text-sm text-white/50 italic">
                      Total Marketing Cost ({formatCurrency(results.totalMarketingCost)}) ÷ Total Sales ({formatNumber(results.totalSales)})
                    </p>
                  </div>

                  {/* Return on Ad Spend */}
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                    <p className="text-base text-white/70 mb-2">Return on Ad Spend</p>
                    <p className="text-3xl sm:text-4xl font-black text-[#0FFFE2] mb-3">{results.roas.toFixed(1)}x</p>
                    <p className="text-sm text-white/60 mb-2">Revenue generated per $1 spent</p>
                    <p className="text-sm text-white/50 italic">
                      Revenue ({formatCurrency(results.revenue)}) ÷ Marketing Cost ({formatCurrency(results.totalMarketingCost)})
                    </p>
                  </div>

                  {/* Net Profit */}
                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                    <p className="text-base text-white/70 mb-2">Net Profit</p>
                    <p className={`text-3xl sm:text-4xl font-black mb-3 ${results.netProfit > 0 ? 'text-[#D0FF0B]' : 'text-[#DF1283]'}`}>
                      {formatCurrency(results.netProfit)}
                    </p>
                    <p className="text-sm text-white/60 mb-2">Revenue minus marketing costs</p>
                    <p className="text-sm text-white/50 italic">
                      Revenue ({formatCurrency(results.revenue)}) - Marketing Cost ({formatCurrency(results.totalMarketingCost)})
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* What-If Analysis - Only for conversion method */}
            {calculationMethod === 'conversion' && (
              <div className="bg-black rounded-3xl p-8 border border-white/10" data-testid="whatif-section">
                <h3 className="text-xl font-bold text-white mb-2">What-If Analysis</h3>
                <p className="text-sm text-white/70 mb-4">See how improving your conversion rates reduces traffic needed</p>
                
                {/* Beginner-friendly instructions */}
                <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-xl p-4 mb-6">
                  <p className="text-sm font-semibold text-[#FF7901] mb-2">💡 How to use this section:</p>
                  <ol className="text-sm text-white/80 space-y-2 list-decimal list-inside">
                    <li>
                      <span className="text-white/80">Your <span className="text-[#0FFFE2] font-semibold">current rates</span> are shown below each input (from your inputs above)</span>
                    </li>
                    <li>
                      <span className="text-white/80">Enter a <span className="text-[#D0FF0B] font-semibold">higher percentage</span> to see what happens if you improve your conversion</span>
                    </li>
                    <li>
                      <span className="text-white/80">The results below will show you how many <span className="text-[#D0FF0B] font-semibold">fewer visitors</span> and <span className="text-[#D0FF0B] font-semibold">less ad spend</span> you'd need</span>
                    </li>
                  </ol>
                  <p className="text-xs text-white/50 mt-3 italic">
                    Example: If your current opt-in rate is 3%, try entering 5% to see how a small improvement impacts your results!
                  </p>
                </div>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-[#0FFFE2] uppercase tracking-wider">
                        Improved {funnelType === 'direct' ? 'Purchase' : 'Opt-in'} Rate (%)
                      </Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder={websiteConversion || '5'}
                        value={improvedWebsiteConversion}
                        onChange={(e) => setImprovedWebsiteConversion(parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0)}
                        className="h-12 bg-white/5 border-2 border-[#0FFFE2]/50 focus:border-[#0FFFE2] text-white text-xl font-bold placeholder:text-white/30 rounded-xl text-center"
                      />
                      <p className="text-xs text-white/50 text-center">Current: {websiteConversion || '0'}%</p>
                    </div>

                    {funnelType === 'optin' && (
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-[#0FFFE2] uppercase tracking-wider">
                          Improved Lead → Sale (%)
                        </Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder={leadConversion || '7'}
                          value={improvedLeadConversion}
                          onChange={(e) => setImprovedLeadConversion(parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0)}
                          className="h-12 bg-white/5 border-2 border-[#0FFFE2]/50 focus:border-[#0FFFE2] text-white text-xl font-bold placeholder:text-white/30 rounded-xl text-center"
                        />
                        <p className="text-xs text-white/50 text-center">Current: {leadConversion || '0'}%</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-white/10">
                    <div className="bg-white/5 rounded-xl p-4 sm:p-5 text-center border border-[#D0FF0B]/20">
                      <p className="text-xs sm:text-sm text-white mb-2">Visitors Needed (Improved)</p>
                      <p className="text-2xl sm:text-3xl font-black text-[#D0FF0B]">{formatNumber(results.improvedVisitors)}</p>
                      {results.totalVisitors > results.improvedVisitors && (
                        <p className="text-xs sm:text-sm text-[#D0FF0B] mt-2 font-semibold">
                          {formatNumber(results.totalVisitors - results.improvedVisitors)} fewer visitors!
                        </p>
                      )}
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 sm:p-5 text-center border border-[#D0FF0B]/20">
                      <p className="text-xs sm:text-sm text-white mb-2">Marketing Cost (Improved)</p>
                      <p className="text-2xl sm:text-3xl font-black text-[#D0FF0B]">{formatCurrency(results.improvedMarketingCost)}</p>
                      {results.totalMarketingCost > results.improvedMarketingCost && (
                        <p className="text-xs sm:text-sm text-[#D0FF0B] mt-2 font-semibold">
                          Save {formatCurrency(results.totalMarketingCost - results.improvedMarketingCost)}!
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Optimization Tips */}
            <div className="bg-black rounded-3xl p-6 sm:p-8 border border-white/10" data-testid="tips-section">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-6">Optimization Tips</h3>
              <ul className="space-y-4">
                {[
                  { color: '#DF1283', text: 'Improve your landing page with clear CTAs and benefits to boost conversion rates' },
                  { color: '#FF7901', text: 'Implement lead nurturing sequences to increase lead-to-sale conversion' },
                  { color: '#0FFFE2', text: 'Consider creating upsells or bundles to increase your average order value' },
                  { color: '#D0FF0B', text: 'Test different ad creatives and targeting to lower your cost per lead' },
                ].map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-4 text-white">
                    <span className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: tip.color }} />
                    <span>{tip.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-white/60 py-12 mt-8 border-t border-white/10">
          <p className="font-semibold text-white/80">© 2025-2026 UnCloned® Media. All Rights Reserved.</p>
          <p className="mt-2 text-xs">UnCloned® is a registered trademark. Unauthorized reproduction or distribution is prohibited.</p>
        </div>
      </div>
    </div>
  );
};

export default OfferCalculator;
